import React, { useRef, useState, useEffect } from "react";
import { Popper, Paper, TextField, IconButton, makeStyles } from "@material-ui/core";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import ClearIcon from "@material-ui/icons/Clear";
import { useConfigs } from "../config-provider";
import List from "./List";
import { useControlled } from "../utils";
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      width: "100%"
    },
    input: {
      width: "100%",
      "& input": {
        paddingTop: 6,
        paddingBottom: 6,
        lineHeight: 16,
        height: 16,
        paddingLeft: 8,
        fontSize: 14
      },
      "&>div": {
        paddingTop: "2px !important",
        paddingBottom: "2px !important"
      },
      "& fieldset": {
        borderRadius: 16,
        borderWidth: "1px !important"
      }
    },
    endAdornment: {
      top: "calc(50% - 14px)",
      right: 8,
      position: "absolute"
    },
    clearButton: {
      marginTop: 1
    },
    noOptions: {
      color: theme.palette.text.secondary,
      padding: "14px 16px"
    },
    popper: {
      zIndex: theme.zIndex.modal + 1
    }
  };
});

function ProAutocomplete(_a) {
  var label = _a.label,
      initLabel = _a.initLabel,
      placeholder = _a.placeholder,
      _b = _a.noArrow,
      noArrow = _b === void 0 ? true : _b,
      request = _a.request,
      startAdornment = _a.startAdornment,
      defaultValue = _a.value,
      _c = _a.options,
      options = _c === void 0 ? [] : _c,
      onChange = _a.onChange,
      renderOption = _a.renderOption;
  var locale = useConfigs().locale;
  var classes = useStyles();
  var pathname = useRef(""); // when select option, blur will trigger after onSelect, detect it

  var changedValue = useRef("");
  var timer = useRef(0);
  var inputRef = useRef(null);
  var textRef = useRef(null);

  var _d = useState(false),
      open = _d[0],
      setOpen = _d[1];

  var _e = useState(false),
      focused = _e[0],
      setFocused = _e[1];

  var _f = useControlled({
    controlled: defaultValue,
    default: ""
  }),
      value = _f[0],
      setValue = _f[1];

  var _g = useState(""),
      inputValue = _g[0],
      setInputValue = _g[1];

  function setInputLabel(v) {
    if (options && options.length !== 0) {
      var option = options.find(function (item) {
        return item.value === v;
      });
      setInputValue(option && option.label || "");
    } else {
      try {
        var storageOptions = JSON.parse(sessionStorage.getItem("turing-ui-autocomplete-options-".concat(pathname.current)) || "");

        if (storageOptions && storageOptions.length > 0) {
          var option = storageOptions.find(function (item) {
            return item.value === v;
          });
          setInputValue(option && option.label || "");
        }
      } catch (e) {}
    }
  }

  useEffect(function () {
    pathname.current = window.location.pathname;

    if (value !== undefined && !initLabel) {
      setInputLabel(value);
    } else if (value !== undefined && initLabel) {
      setInputValue(initLabel);
    }
  }, [initLabel]);
  useEffect(function () {
    return function () {
      if (options.length > 0) {
        sessionStorage.setItem("turing-ui-autocomplete-options-".concat(pathname.current), JSON.stringify(options));
      } else if (value && initLabel) {
        sessionStorage.setItem("turing-ui-autocomplete-options-".concat(pathname.current), JSON.stringify([{
          value: value,
          label: initLabel
        }]));
      }
    };
  }, [options, value, initLabel]);
  useEffect(function () {
    if (!value) {
      setInputValue("");
    }
  }, [value]);

  var _h = useState(1),
      refresh = _h[1];

  useEffect(function () {
    refresh(0);
  }, []);

  function handleSelect(option) {
    setOpen(false);
    setValue(option.value);
    setInputLabel(option.value);
    changedValue.current = option.value;
    onChange && onChange(option.value, option);
  }

  function handleInputChange(e) {
    setOpen(true);
    setInputValue(e.target.value);

    if (!e.target.value) {
      onChange && onChange("", null);
    }

    var cacheValue = e.target.value;

    if (request) {
      if (timer.current) {
        clearTimeout(timer.current);
      }

      timer.current = window.setTimeout(function () {
        request(cacheValue);
      }, 500);
    }
  }

  function handleClear() {
    setValue("");
    setInputValue("");
    onChange && onChange("", null);
  }

  function handleBlur() {
    setFocused(false);
    setOpen(false);

    if (changedValue.current) {
      changedValue.current = "";
    } else {
      setInputLabel(value);
    }
  }

  function handleFocus() {
    setFocused(true);

    if (inputValue) {
      setOpen(true);
    }
  }

  function handleMouseEnter() {
    setFocused(true);
  }

  function handleMouseLeave() {
    setFocused(false);
  }

  function handleDropdown() {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }

  return React.createElement(React.Fragment, null, React.createElement("div", {
    ref: textRef,
    className: classes.root
  }, React.createElement(TextField, {
    className: classes.input,
    label: label,
    placeholder: placeholder,
    value: inputValue,
    size: "small",
    variant: "outlined",
    onChange: handleInputChange,
    onBlur: handleBlur,
    onFocus: handleFocus,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    InputProps: {
      style: {
        paddingRight: !noArrow ? 65 : 32,
        paddingLeft: 8
      },
      startAdornment: startAdornment,
      endAdornment: React.createElement("div", {
        className: classes.endAdornment
      }, focused && value && React.createElement(IconButton, {
        onMouseDown: handleClear,
        size: "small",
        className: classes.clearButton
      }, React.createElement(ClearIcon, {
        fontSize: "small"
      })), !noArrow && React.createElement(IconButton, {
        onMouseDown: handleDropdown,
        size: "small"
      }, React.createElement(ArrowDropDown, null)))
    },
    inputProps: {
      ref: inputRef
    }
  })), open && textRef && textRef.current && React.createElement(Popper, {
    style: {
      width: textRef ? textRef.current.clientWidth : "auto",
      marginTop: 4
    },
    className: classes.popper,
    anchorEl: textRef.current,
    open: true
  }, React.createElement(Paper, null, options.length === 0 ? React.createElement("div", {
    className: classes.noOptions
  }, locale.common.noOptions) : React.createElement(List, {
    options: options,
    selected: value,
    renderOption: renderOption,
    onSelect: handleSelect
  }))));
}

export default ProAutocomplete;